<template>
  <div class="relative py-4">
    <!-- 섹션 헤더 (제목 + 우측: 모바일 접기/펼치기, 데스크탑 버튼) -->
    <vs-row v-if="!!title || (controls && !readonly && !isPrint)" class="flex items-center">
      <h5 v-if="!!title" class="flex items-center mb-4"><i class="dot"></i>{{ $t(title) }}</h5>
      <div class="ml-auto mb-4 flex items-center">
        <!-- 모바일: 접기/펼치기 토글 (인쇄 시 숨김) -->
        <div v-if="!isPrint" class="xl:hidden collapse-toggle">
          <span :class="{ active: !collapsed }" @click="collapsed = false">{{ $t("펼치기") }} ▼</span>
          <span class="separator">|</span>
          <span :class="{ active: collapsed }" @click="collapsed = true">{{ $t("접기") }} ▲</span>
        </div>
        <!-- 데스크탑: 항목추가 / 선택삭제 -->
        <div v-if="controls && !readonly && !isPrint" class="hidden xl:flex items-center">
          <vs-button color="secondary" type="filled" class="ml-4 mt-1" @click="handleAddRow">{{ $t(buttonAddName) }}</vs-button>
          <vs-button color="secondary" type="border" :disabled="list.length < 1 || checkData.length === 0" class="ml-4 mt-1" @click="handleRemoveRow">{{ $t(buttonRemoveName) }}</vs-button>
        </div>
      </div>
    </vs-row>

    <!-- 모바일: 버튼 행 (헤더 아래 별도) -->
    <div v-if="controls && !readonly && !isPrint" class="xl:hidden flex justify-end gap-2 mb-4">
      <vs-button color="secondary" type="filled" @click="handleAddRow">{{ $t(buttonAddName) }}</vs-button>
      <vs-button color="secondary" type="border" :disabled="list.length < 1 || checkData.length === 0" @click="handleRemoveRow">{{ $t(buttonRemoveName) }}</vs-button>
    </div>

    <!-- 모바일: 카드 뷰 (인쇄 시 렌더하지 않음 → 데스크탑 테이블로 통일) -->
    <div v-if="!isPrint" v-show="!collapsed" class="xl:hidden mobile-view">
      <!-- 전체선택 행 -->
      <div v-if="multiple && !readonly && !isPrint" class="all-select-row">
        <span class="all-select-title">{{ $t(title || "참석자 명단") }}</span>
        <label class="all-select-label">
          <span>{{ $t("전체선택") }}</span>
          <vs-checkbox :value="allChecked" @click.native="onClickAllCheckbox" class="mx-0" />
        </label>
      </div>

      <!-- 카드 -->
      <div v-for="(item, idx) in list" :key="`m_${item.id}`" class="attendee-card">
        <div class="card-header">
          <span class="index">({{ idx + 1 }})</span>
          <vs-checkbox v-if="multiple && !readonly && !isPrint" v-model="checkData" :vs-value="item.id" class="mx-0" />
        </div>
        <div class="card-body">
          <div class="field">
            <label class="field-label">{{ $t("소속 / 직책") }}</label>
            <div class="field-input">
              <template v-if="isPrint">
                <div class="print-input">{{ item.소속 }}</div>
              </template>
              <ml-input v-else v-model="item.소속" :readonly="readonly" :placeholder="$t('OO센터 / 과장')" class="w-full" />
            </div>
          </div>
          <div class="field">
            <label class="field-label">{{ $t("성명") }}</label>
            <div class="field-input">
              <template v-if="isPrint">
                <div class="print-input">{{ item.성명 }}</div>
              </template>
              <ml-input v-else v-model="item.성명" :readonly="readonly" :placeholder="$t('홍길동')" class="w-full" />
            </div>
          </div>
          <div class="field field-sign">
            <label class="field-label">{{ $t("서명") }}</label>
            <div class="field-input">
              <template v-if="isPrint">
                <div style="text-align: -webkit-center">
                  <img v-if="item.서명" :src="item.서명" alt="Signature" class="signatureImg" />
                  <div v-else class="print-input sign-empty"></div>
                </div>
              </template>
              <sign-field v-else v-model="item.서명" :readonly="readonly" />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 데스크탑: 2열 테이블 (xl+ 에서 노출, 인쇄 시에는 화면폭과 무관하게 항상 노출) -->
    <table :class="{ 'hidden xl:table': !isPrint }">
      <colgroup>
        <col v-if="multiple && !isPrint && !readonly" width="60" />
        <col />
        <col />
        <col />
        <col v-if="multiple && !isPrint && !readonly" width="60" />
        <col />
        <col />
        <col />
      </colgroup>
      <thead>
        <tr>
          <th v-if="multiple && !isPrint && !readonly" class="table-cell">
            <vs-checkbox :value="allChecked" class="justify-center mx-0" @click.native="onClickAllCheckbox" />
          </th>
          <th class="table-cell">
            <div class="w-full text-center">{{ $t("소속/직책") }}</div>
          </th>
          <th class="table-cell">
            <div class="w-full text-center">{{ $t("성명") }}</div>
          </th>
          <th class="table-cell">
            <div class="w-full text-center">{{ $t("서명") }}</div>
          </th>
          <th v-if="multiple && !isPrint && !readonly" class="table-cell pair-divider"></th>
          <th class="table-cell" :class="!(multiple && !isPrint && !readonly) ? 'pair-divider' : ''">
            <div class="w-full text-center">{{ $t("소속/직책") }}</div>
          </th>
          <th class="table-cell">
            <div class="w-full text-center">{{ $t("성명") }}</div>
          </th>
          <th class="table-cell">
            <div class="w-full text-center">{{ $t("서명") }}</div>
          </th>
        </tr>
      </thead>
      <tbody v-for="(pair, i) in pairs" :key="`pair_${i}`">
        <tr class="print-container">
          <!-- 좌측 attendee 체크박스 -->
          <td v-if="multiple && !isPrint && !readonly">
            <vs-checkbox v-if="pair[0]" v-model="checkData" :vs-value="pair[0].id" class="justify-center mx-0" />
          </td>
          <!-- 좌측 attendee 셀 -->
          <td class="table-cell align-top text-center">
            <template v-if="isPrint">
              <div class="print-input">{{ pair[0] && pair[0].소속 }}</div>
            </template>
            <ml-input v-else v-model="pair[0].소속" :readonly="readonly" :placeholder="$t('OO센터 / 과장')" class="w-full flex-1" />
          </td>
          <td class="table-cell align-top text-center">
            <template v-if="isPrint">
              <div class="print-input">{{ pair[0] && pair[0].성명 }}</div>
            </template>
            <ml-input v-else v-model="pair[0].성명" :readonly="readonly" :placeholder="$t('홍길동')" class="w-full flex-1" />
          </td>
          <td class="table-cell align-top text-center">
            <template v-if="isPrint">
              <div style="text-align: -webkit-center">
                <img v-if="pair[0] && pair[0].서명" :src="pair[0].서명" alt="Signature" class="signatureImg" />
                <div v-else style="width: 100%; height: 80px; min-width: 80px; max-width: 120px" class="print-input"></div>
              </div>
            </template>
            <sign-field v-else v-model="pair[0].서명" :readonly="readonly" />
          </td>

          <!-- 우측 attendee 체크박스 -->
          <td v-if="multiple && !isPrint && !readonly" class="table-cell pair-divider">
            <vs-checkbox v-if="pair[1]" v-model="checkData" :vs-value="pair[1].id" class="justify-center mx-0" />
          </td>
          <!-- 우측 attendee 셀 (홀수 명일 때 마지막 행은 pair[1] === null → 빈 셀) -->
          <td class="table-cell align-top text-center" :class="!(multiple && !isPrint && !readonly) ? 'pair-divider' : ''">
            <template v-if="pair[1]">
              <template v-if="isPrint">
                <div class="print-input">{{ pair[1].소속 }}</div>
              </template>
              <ml-input v-else v-model="pair[1].소속" :readonly="readonly" :placeholder="$t('OO센터 / 과장')" class="w-full flex-1" />
            </template>
          </td>
          <td class="table-cell align-top text-center">
            <template v-if="pair[1]">
              <template v-if="isPrint">
                <div class="print-input">{{ pair[1].성명 }}</div>
              </template>
              <ml-input v-else v-model="pair[1].성명" :readonly="readonly" :placeholder="$t('홍길동')" class="w-full flex-1" />
            </template>
          </td>
          <td class="table-cell align-top text-center">
            <template v-if="pair[1]">
              <template v-if="isPrint">
                <div style="text-align: -webkit-center">
                  <img v-if="pair[1].서명" :src="pair[1].서명" alt="Signature" class="signatureImg" />
                  <div v-else style="width: 100%; height: 80px; min-width: 80px; max-width: 120px" class="print-input"></div>
                </div>
              </template>
              <sign-field v-else v-model="pair[1].서명" :readonly="readonly" />
            </template>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import SignField from "@/components/SignField.vue";

export default {
  name: "AttendeeTableTwoColumn",
  components: { SignField },
  props: {
    isPrint: Boolean,
    board: Object,
    title: {
      type: String,
      default: "참석자 명단",
    },
    value: {
      type: Array,
      default: () => [],
    },
    controls: {
      type: Boolean,
      default: true,
    },
    multiple: {
      type: Boolean,
      default: true,
    },
    buttonAddName: {
      type: String,
      default: "항목추가",
    },
    buttonRemoveName: {
      type: String,
      default: "선택삭제",
    },
  },
  emits: ["input"],
  data() {
    return {
      list: this.value,
      checkData: [],
      collapsed: false,
    };
  },
  computed: {
    readonly() {
      return !!(this.board && this.board.readonly);
    },
    pairs() {
      const result = [];
      for (let i = 0; i < this.list.length; i += 2) {
        result.push([this.list[i], this.list[i + 1] || null]);
      }
      return result;
    },
    allChecked() {
      return this.list.length > 0 && this.checkData.length === this.list.length;
    },
  },
  watch: {
    value: {
      handler(value) {
        this.list = value;
      },
    },
    list: {
      deep: true,
      handler(value) {
        this.$emit("input", value);
      },
    },
  },
  mounted() {
    if (!this.isPrint && this.list.length < 1) {
      this.handleAddRow();
    }
  },
  methods: {
    generateId() {
      return `${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
    },
    handleAddRow() {
      this.list.push({ id: this.generateId() });
    },
    handleRemoveRow() {
      this.list = this.list.filter((item) => !this.checkData.includes(item.id));
      this.checkData = [];
    },
    onClickAllCheckbox() {
      if (this.checkData.length === this.list.length) this.checkData = [];
      else this.checkData = this.list.map((item) => item.id);
    },
  },
};
</script>

<style scoped lang="scss">
$border-color: #cdcdcd;
$primary: #7367f0;

table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;

  thead {
    background: #f9f9f9;
    z-index: 0 !important;

    th {
      font-style: normal;
      font-weight: 500;
      font-size: 14px;
      line-height: 18px;
      color: #3c3c3c;

      border-top: solid 1px $border-color;
      border-bottom: solid 1px $border-color;
      padding: 16px 0;
    }
  }

  tbody {
    tr {
      background: #fff !important;
      border-bottom: solid 1px $border-color;

      td {
        padding: 6px;
      }
    }
  }
}

.pair-divider {
  border-left: 1px solid $border-color;
}

.signatureImg {
  border: 1px solid #00000033;
  border-radius: 6px;
  min-height: 80px;
  max-width: 120px;
}

div::v-deep .input-span-placeholder {
  text-align: left;
}

/* ────── 모바일 전용 ────── */
.collapse-toggle {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #8c8c8c;
  user-select: none;

  span {
    cursor: pointer;

    &.active {
      color: $primary;
      font-weight: 500;
    }
  }

  .separator {
    color: $border-color;
    cursor: default;
  }
}

.mobile-view {
  .all-select-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 12px;
    border-top: 1px solid $border-color;
    border-bottom: 1px solid $border-color;

    .all-select-title {
      font-weight: 500;
      font-size: 14px;
      color: #3c3c3c;
    }

    .all-select-label {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 14px;
      color: #3c3c3c;
      cursor: pointer;
      margin-bottom: 0;
    }
  }

  .attendee-card {
    border: 1px solid $border-color;
    border-radius: 8px;
    margin-top: 12px;
    overflow: hidden;
    background: #fff;

    .card-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 10px 16px;
      background: #f9f9f9;
      border-bottom: 1px solid $border-color;

      .index {
        font-weight: 500;
        font-size: 14px;
        color: #3c3c3c;
      }
    }

    .card-body {
      padding: 16px;

      .field {
        display: flex;
        align-items: center;
        margin-bottom: 12px;

        &:last-child {
          margin-bottom: 0;
        }

        .field-label {
          width: 80px;
          flex-shrink: 0;
          font-size: 14px;
          color: #3c3c3c;
          margin-bottom: 0;
        }

        .field-input {
          flex: 1;
          min-width: 0;
        }

        &.field-sign {
          align-items: flex-start;

          .field-label {
            padding-top: 8px;
          }
        }
      }
    }
  }
}

.sign-empty {
  width: 100%;
  height: 80px;
  min-width: 80px;
  max-width: 120px;
}
</style>
